import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons'
import { useQueryClient } from '@tanstack/react-query'
import type { ReactElement } from 'react'
import { useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { usePathViewedActivity } from '~/api/queries/pathViewed'
import { TextInput } from '~/components/Form'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Button } from '~/components/Global/Interactions/Button/Button'
import { ButtonVariation } from '~/components/Global/Interactions/Button/ButtonVariation'
import { useInfiniteList } from '~/hooks/useInfiniteList'
import { useConversations } from '../../queries/useConversations'
import type { ConversationEntityType, SelectedConversation } from '../../types'
import { ConversationItem } from '../ConversationItem/ConversationItem'
import { ConversationItemShimmer } from '../ConversationItem/ConversationItemShimmer'
import { ConversationListOptions } from '../ConversationListOptions/ConversationListOptions'
import {
  conversationListWrapper,
  conversationsList,
  filterButton,
  listHeader,
  searchRow
} from './ConversationList.css'

type FilterType = ConversationEntityType | 'all'

const FILTERS: { key: FilterType; label: string }[] = [
  { key: 'all', label: 'Tous' },
  { key: 'applicant', label: 'Candidats' },
  { key: 'contact', label: 'Contacts' }
]

type ConversationListProps = {
  selected: SelectedConversation | null
  onSelect: (conversation: SelectedConversation) => void
}

export const ConversationList = ({
  selected,
  onSelect
}: ConversationListProps): ReactElement => {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<FilterType>('all')
  const [includeCampaigns, setIncludeCampaigns] = useState(false)
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const debounceSearch = useDebouncedCallback(setDebouncedSearch, 300)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
    debounceSearch(e.target.value)
  }

  const {
    items,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useConversations({
    search: debouncedSearch,
    entityType: filter,
    includeCampaigns,
    unreadOnly
  })

  // A message on any conversation the user follows moves `/messaging`: the list is refetched
  // to pick up the new order and excerpt.
  const queryClient = useQueryClient()
  usePathViewedActivity('/messaging', {
    onNewActivity: () => {
      void queryClient.invalidateQueries({
        queryKey: ['messaging', 'conversations']
      })
    }
  })

  const { lastItemRef } = useInfiniteList({
    onEndOfListReached: () => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage()
    }
  })

  return (
    <div className={conversationListWrapper}>
      <div className={listHeader}>
        <ButtonGroup>
          {FILTERS.map(({ key, label }) => (
            <Button
              key={key}
              type="button"
              buttonSize="small"
              className={filterButton}
              variation={
                filter === key
                  ? ButtonVariation.Primary
                  : ButtonVariation.Neutral
              }
              onClick={() => setFilter(key)}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>
        <div className={searchRow}>
          <TextInput
            standalone
            placeholder="Rechercher..."
            type="search"
            name="search"
            value={search}
            leadingIcon={faMagnifyingGlass}
            small
            onChange={handleSearchChange}
            wrapper={false}
          />
          <ConversationListOptions
            unreadOnly={unreadOnly}
            includeCampaigns={includeCampaigns}
            onUnreadOnlyChange={setUnreadOnly}
            onIncludeCampaignsChange={setIncludeCampaigns}
          />
        </div>
      </div>

      <div className={conversationsList}>
        {isLoading &&
          Array.from({ length: 6 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: shimmer placeholder items have no natural key
            <ConversationItemShimmer key={i} />
          ))}

        {isError && (
          <NoResult
            message="Impossible de charger les conversations"
            isError
            isSmall
          />
        )}

        {!isLoading && !isError && items?.length === 0 && (
          <NoResult message="Aucune conversation" isSmall />
        )}

        {items?.map((conversation) => (
          <ConversationItem
            key={`${conversation.type}-${conversation.id}`}
            conversation={conversation}
            showType={filter === 'all'}
            isActive={
              selected?.type === conversation.type &&
              selected?.id === conversation.id
            }
            onClick={() =>
              onSelect({ type: conversation.type, id: conversation.id })
            }
          />
        ))}

        {hasNextPage && (
          <div ref={lastItemRef}>{isFetchingNextPage && <Loader small />}</div>
        )}
      </div>
    </div>
  )
}
